import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useAdvancementsToAwardReport } from '@modules/advancement/pendingItems/hooks';
import { advancementReportResponse } from '@modules/advancementReport/duck/actions';
import Modal from '@shared/components/Modal';

import {
  advancementReportModalOpenSel,
  cancelAdvancementReportModal,
  editedReportSel,
  goToReports,
  loadingSel,
  reportSel,
  selectedUserIdsSel,
} from '../../duck';
import AdvancementReportForm from './AdvancementReportForm/AdvancementReportForm';
import FallbackMessage from './FallbackMessage';

interface AdvancementReportModalProps {
  visible: boolean;
  loading: boolean;
  items: unknown[];
  canEditMultipleItems: boolean;
  selectedUserIds: number[];
  onCancel: () => void;
  onGoToReports: () => void;
}

function AdvancementReportModal(props: AdvancementReportModalProps) {
  const {
    visible,
    onCancel,
    items,
    loading,
    canEditMultipleItems,
    selectedUserIds,
    onGoToReports,
  } = props;

  const areItemsAvailable = loading || items.length > 0;

  return (
    <Modal
      headerColor="scouting-warm-gray"
      visible={visible}
      title={<FormattedMessage id="packRoster.AdvancementReportModal.title" />}
      onCancel={onCancel}
      backTitle={
        <FormattedMessage id="packRoster.AdvancementReportModal.back" />
      }
      size="large"
      noPadding
    >
      <React.Fragment>
        {areItemsAvailable ? (
          <AdvancementReportForm
            items={items}
            loading={loading}
            canEditMultipleItems={canEditMultipleItems}
          />
        ) : (
          <FallbackMessage
            youthCount={selectedUserIds.length}
            onGoToReports={onGoToReports}
          />
        )}
      </React.Fragment>
    </Modal>
  );
}

const AdvancementReportModalContainer: React.FC = () => {
  const dispatch = useDispatch();
  const visible = useSelector(advancementReportModalOpenSel);
  const loading = useSelector(loadingSel);
  const items = useSelector(editedReportSel);
  const canEditMultipleItems = useSelector(reportSel)?.length > 1;
  const selectedUserIds = useSelector(selectedUserIdsSel);
  const query = useAdvancementsToAwardReport({ skip: !visible });

  useEffect(() => {
    dispatch(advancementReportResponse(query.data || []));
  }, [query.data, dispatch]);

  const onCancel = () => dispatch(cancelAdvancementReportModal());
  const onGoToReports = () => dispatch(goToReports());

  return (
    <AdvancementReportModal
      canEditMultipleItems={canEditMultipleItems}
      loading={loading || query.isFetching}
      visible={visible}
      onGoToReports={onGoToReports}
      onCancel={onCancel}
      selectedUserIds={selectedUserIds}
      items={items}
    />
  );
};

export default AdvancementReportModalContainer;
